import React, { useState, useEffect, useCallback, useRef } from "react";
import TextInput from "uicomponents/TextInput";
import SelectInput from "uicomponents/SelectInput";
import { stateOption } from "../../pages/WorkersCompApplcation/utils/selectInputOptions";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import styles from "./Address.module.css";
import { useFormikContext } from "formik";
import { BusinessAddressFormType } from "./FormUtils";
import { useOktaAuth } from "@okta/okta-react";
import { getWWRedirectUrl } from "utils/helper";
import { trackFormError } from "utils/analytics/trackEvent";

interface IBussinessAddressComponentProps {
  hasMargin?: boolean;
  isPrimaryAddress?: boolean;
  fieldRefs?: React.MutableRefObject<{
    [key: string]: HTMLInputElement | null;
  }>;
}

interface countyOptions {
  description: string;
  value: string;
}

const BussinessAddressComponent = ({
  ...props
}: IBussinessAddressComponentProps): JSX.Element => {
  const { hasMargin = false, fieldRefs, isPrimaryAddress } = props;
  const labelList = sectionsQuestions["officeLocation"].questions;
  const messageList = sectionsQuestions["officeLocation"].message;
  const { authState } = useOktaAuth();
  const { values, errors, setFieldValue } = useFormikContext();

  const hasZip = (values as BusinessAddressFormType)?.zip;
  const hasState = (values as BusinessAddressFormType)?.state;
  const prevHasZip = useRef(hasZip);
  const prevHasState = useRef(hasState);

  const hasStateOrZipError = (errors as any)?.zip || (errors as any)?.state;

  const [countyOptions, setCountyOptions] = useState<countyOptions[]>([]);

  const fetchcountyOptions = useCallback(async () => {
    const getState = stateOption?.filter((state) => state.value === hasState)[0]
      ?.description;
    const urlString = `/v1/county/${hasZip}?state=${getState}`;
    const fetchUrl = getWWRedirectUrl(urlString);

    try {
      const response = await fetch(fetchUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authState?.accessToken?.accessToken,
        },
      });

      if (!response.ok) {
        return [];
      }
      const result = await response.json();

      if (!result.length) {
        trackFormError(
          Error("Empty County Dropdown"),
          "No County found for given zip and state"
        );
      }

      const setcountyOptionsFromResponse = result.map((data: any) => {
        return { value: data.name, description: data.name };
      });

      setCountyOptions(setcountyOptionsFromResponse);
    } catch (error: any) {
      trackFormError(error, "Error fetching county options");
    }
  }, [authState, hasState, hasZip]);

  useEffect(() => {
    if (hasZip && hasState && !hasStateOrZipError) {
      fetchcountyOptions();
    }
  }, [hasZip, hasState, hasStateOrZipError, fetchcountyOptions]);

  useEffect(() => {
    if (hasZip !== prevHasZip.current || hasState !== prevHasState.current) {
      setFieldValue("subdivision", "");
    }
  }, [hasZip, hasState, setFieldValue]);

  return (
    <div>
      <TextInput
        name="locationName"
        type="string"
        label={labelList.locationName}
        fieldRefs={fieldRefs}
      />
      <TextInput
        name="address1"
        type="string"
        label={labelList.address1}
        message={messageList.address1}
        fieldRefs={fieldRefs}
      />
      <TextInput
        name="address2"
        type="string"
        label={labelList.address2}
        required={false}
      />
      {isPrimaryAddress ? (
        <>
          <div className={styles.addressFlexDivWithSubdivision}>
            <TextInput
              name="city"
              type="string"
              label={labelList.city}
              fieldRefs={fieldRefs}
            />
            <SelectInput
              name="state"
              options={stateOption}
              label={labelList.state}
              placeholder="Select an option"
              fieldRefs={fieldRefs}
            />
          </div>
          <div className={styles.addressFlexDivWithSubdivision}>
            <TextInput
              name="zip"
              type="string"
              label={labelList.zip}
              fieldRefs={fieldRefs}
            />
            <SelectInput
              name="subdivision"
              label={labelList.subdivision}
              fieldRefs={fieldRefs}
              options={countyOptions}
              disabled={!(hasState || hasZip) || hasStateOrZipError}
              placeholder="Select an option"
            />
          </div>
        </>
      ) : (
        <div className={hasMargin ? styles.addressFlexDiv : ""}>
          <TextInput
            className={hasMargin ? styles.addressFlexDivCity : ""}
            name="city"
            type="string"
            label={labelList.city}
            fieldRefs={fieldRefs}
          />
          <SelectInput
            name="state"
            options={stateOption}
            label={labelList.state}
            placeholder="Select an option"
            className={hasMargin ? styles.addressFlexDivState : ""}
            fieldRefs={fieldRefs}
          />
          <TextInput
            name="zip"
            type="string"
            label={labelList.zip}
            className={hasMargin ? styles.addressFlexDivZip : ""}
            fieldRefs={fieldRefs}
          />
        </div>
      )}
    </div>
  );
};

export default BussinessAddressComponent;
